export type Run = {
  runNodeID: string;
  entityName: string;
  projectName: string;
  runName: string;
  displayName?: string;
  jobType?: string;
};

export type Artifact = {
  id: string;
  artifactNodeID: string;
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactSequenceName: string;
  artifactCommitHash: string;
  versionIndex: number;
};

export enum Direction {
  TowardArtifact,
  AwayFromArtifact,
}

export const TOWARD_ARTIFACT: string = 'TowardArtifact';

export type Edge = {
  edgeNodeID: string;
  artifactNodeID: string;
  runNodeID: string;
  clusterID: string;
  direction: Direction;
};

export type Cluster = {
  nodeType: string;
  id: string;
  artifactNodes: Artifact[];
  runNodes: Run[];
  clusterEdges: Edge[];
};

export enum LineageQueryType {
  RUN,
  ARTIFACT,
}

export function formatArtifactDagQuery(
  data: any,
  lineageQueryType: LineageQueryType
) {
  let lineageData: any;
  if (lineageQueryType === LineageQueryType.ARTIFACT) {
    lineageData = data?.artifact?.artifactLineageDag;
  } else {
    lineageData = data?.project?.run?.artifactLineageDag;
  }

  // Reformat artifacts response
  const artResp = lineageData?.artifacts;
  const dagArtifacts: {[id: string]: Artifact} = {};
  if (artResp != null) {
    for (const a of artResp) {
      if (a != null) {
        const art = {
          id: a.id,
          artifactNodeID: a.artifactNodeID,
          entityName: a.entityName,
          projectName: a.projectName,
          artifactTypeName: a.artifactTypeName,
          artifactSequenceName: a.artifactSequenceName,
          artifactCommitHash: a.artifactCommitHash,
          versionIndex: a.versionIndex,
        };
        const artID = a.artifactNodeID;
        dagArtifacts[artID] = art;
      }
    }
  }

  // Reformat edges response
  const edgeResp = lineageData?.edges;
  const dagEdges: {[id: string]: Edge} = {};
  if (edgeResp != null) {
    for (const e of edgeResp) {
      if (e != null) {
        const dirEnum =
          e.direction === TOWARD_ARTIFACT
            ? Direction.TowardArtifact
            : Direction.AwayFromArtifact;
        const edge = {
          edgeNodeID: e.edgeID,
          artifactNodeID: e.artifactNodeID,
          runNodeID: e.runNodeID,
          clusterID: e.clusterNodeID ?? '',
          direction: dirEnum,
        };
        const eID = e.edgeID;
        dagEdges[eID] = edge;
      }
    }
  }

  // Reformat runs response
  const runResp = lineageData?.runs;
  const dagRuns: {[id: string]: Run} = {};
  if (runResp != null) {
    for (const r of runResp) {
      if (r != null) {
        const run = {
          runNodeID: r.runNodeID,
          entityName: r.entityName,
          projectName: r.projectName,
          runName: r.runName,
          displayName: r.displayName!,
          jobType: r.jobType!,
        };
        const rID = r.runNodeID;
        dagRuns[rID] = run;
      }
    }
  }

  // Reformat clusters response
  const clusterResp = lineageData?.clusters;
  const dagClusters: {[id: string]: Cluster} = {};
  if (clusterResp != null) {
    for (const c of clusterResp) {
      if (c != null) {
        const tempArtNodes =
          c.artifactNodes?.filter(
            (artNode: any) =>
              // eslint-disable-next-line no-prototype-builtins
              !dagArtifacts.hasOwnProperty(artNode?.artifactNodeID)
          ) ?? [];
        const tempRunNodes =
          c.runNodes?.filter(
            // eslint-disable-next-line no-prototype-builtins
            (runNode: any) => !dagRuns.hasOwnProperty(runNode?.runNodeID)
          ) ?? [];

        const tempEdgeNodes = c.clusterEdges ?? [];
        const cluster = {
          nodeType: c.nodeType,
          id: c.id,
          artifactNodes: tempArtNodes as Artifact[],
          runNodes: tempRunNodes as Run[],
          clusterEdges: tempEdgeNodes as Edge[],
        };
        const clusterID = c.id;

        dagClusters[clusterID] = cluster;
      }
    }
  }

  const hitLimit = !!lineageData?.hitLimit;

  return {
    dag: {
      artifacts: dagArtifacts,
      edges: dagEdges,
      runs: dagRuns,
      clusters: dagClusters,
    },
    limited: hitLimit,
  };
}

export type Dag = ReturnType<typeof formatArtifactDagQuery>['dag'];
